import { supabase } from '@/shared/lib/supabase';
import { logger } from '@/shared/lib/logger';
import { AppError } from '@/shared/lib/errors';

export const moderationKeys = {
  all: ['moderation'] as const,
  pending: () => [...moderationKeys.all, 'pending'] as const,
};

export interface ModerationItem {
  id: string;
  entity_type: 'category' | 'merchant';
  entity_id: string;
  entity_name: string;
  entity_icon: string | null;
  // Sprint J.0: para merchants, el default_category_id actual (o null si
  // no hay). La UI de moderación permite cambiarlo desde el propio card.
  default_category_id?: string | null;
  default_category_name?: string | null;
  created_by: string | null;
  created_by_name: string | null;
  created_at: string;
  similar: { id: string; name: string; icon: string | null; usage_count: number; similarity?: number }[];
}

interface EdgeResp<T> { ok: boolean; data?: T; error?: { code: string; message: string } }

/**
 * Devuelve items pendientes con "similares" para sugerir merges.
 * La búsqueda de similares usa pg_trgm ILIKE en el nombre normalizado.
 */
/** Cuántos pendientes se piden de una vez. El mismo tamaño que usa la app. */
export const MODERATION_PAGE_SIZE = 20;

/**
 * Los pendientes de moderar, **de veinte en veinte**.
 *
 * Antes se pedía la cola entera sin `limit`, y por cada fila se lanzaba además
 * una consulta de parecidos: con los 932 pendientes que dejó el import, abrir
 * la pantalla eran 932 llamadas a `pg_trgm` en paralelo y 932 tarjetas
 * pintadas. Hoy la cola tiene 3 —lo importado ya no entra (ADR-0059)—, pero el
 * código seguía sin tope, así que el problema volvía con el siguiente import
 * grande.
 *
 * Se devuelve también el total para poder decir cuántos quedan sin traérselos.
 */
export async function fetchPendingModeration(
  limit: number = MODERATION_PAGE_SIZE,
  offset = 0,
): Promise<{ items: ModerationItem[]; total: number }> {
  // Cargar items sin revisar
  const { data: queueRows, error: qErr, count } = await supabase
    .from('moderation_queue')
    .select('id, entity_type, entity_id, created_by, created_at', { count: 'exact' })
    .is('reviewed_at', null)
    .order('created_at', { ascending: true })
    .range(offset, offset + limit - 1);

  if (qErr) {
    logger.error('fetchPendingModeration failed', { code: qErr.code });
    throw new Error(qErr.message);
  }

  const total = count ?? 0;
  if (!queueRows || queueRows.length === 0) return { items: [], total };

  // Separar categorías y comercios para batch queries
  const catIds = queueRows.filter((r) => r.entity_type === 'category').map((r) => r.entity_id);
  const mrcIds = queueRows.filter((r) => r.entity_type === 'merchant').map((r) => r.entity_id);
  const creators = Array.from(new Set(queueRows.map((r) => r.created_by).filter((v): v is string => v !== null)));

  const [
    { data: cats },
    { data: mrcs },
    { data: participants },
  ] = await Promise.all([
    catIds.length > 0
      ? supabase.from('categories').select('id, name, name_normalized, icon').in('id', catIds)
      : Promise.resolve({ data: [] as { id: string; name: string; name_normalized: string; icon: string }[] }),
    mrcIds.length > 0
      ? supabase.from('merchants').select('id, name, name_normalized, default_category_id, default_category:categories(name)').in('id', mrcIds)
      : Promise.resolve({ data: [] as { id: string; name: string; name_normalized: string }[] }),
    creators.length > 0
      ? supabase.from('participants').select('id, display_name').in('id', creators)
      : Promise.resolve({ data: [] as { id: string; display_name: string }[] }),
  ]);

  const creatorMap = new Map((participants ?? []).map((p) => [p.id, p.display_name]));
  const catMap = new Map((cats ?? []).map((c) => [c.id, c]));
  const mrcMap = new Map((mrcs ?? []).map((m) => [m.id, m]));

  // Similares por item. Las búsquedas van EN PARALELO: encadenarlas en un bucle
  // secuencial convertía la pantalla en N viajes de ida y vuelta (un RPC por
  // pendiente), así que la espera crecía linealmente con la cola.
  const simsByRow = await Promise.all(
    queueRows.map(async (row) => {
      const rpc = row.entity_type === 'category' ? 'find_similar_categories' : 'find_similar_merchants';
      const entity = row.entity_type === 'category' ? catMap.get(row.entity_id) : mrcMap.get(row.entity_id);
      if (!entity) return null;
      const { data } = await supabase.rpc(rpc, {
        p_pending_id: entity.id,
        p_threshold: 0.3,
        p_limit: 5,
      });
      return (data ?? []) as { id: string; name: string; icon?: string | null; similarity: number }[];
    })
  );

  const results: ModerationItem[] = [];

  queueRows.forEach((row, i) => {
    const sims = simsByRow[i];
    if (row.entity_type === 'category') {
      const cat = catMap.get(row.entity_id);
      if (!cat) return;
      results.push({
        id: row.id,
        entity_type: 'category',
        entity_id: cat.id,
        entity_name: cat.name,
        entity_icon: cat.icon,
        created_by: row.created_by,
        created_by_name: row.created_by ? creatorMap.get(row.created_by) ?? null : null,
        created_at: row.created_at,
        similar: (sims ?? []).map((s) => ({
          id: s.id,
          name: s.name,
          icon: s.icon ?? null,
          usage_count: 0,
          similarity: s.similarity,
        })),
      });
    } else {
      const mrc = mrcMap.get(row.entity_id);
      if (!mrc) return;
      // Sprint J.0: destructura la categoría default embebida (join en el select).
      const mrcAny = mrc as unknown as {
        default_category_id: string | null;
        default_category: { name: string } | null;
      };
      results.push({
        id: row.id,
        entity_type: 'merchant',
        entity_id: mrc.id,
        entity_name: mrc.name,
        entity_icon: null,
        default_category_id: mrcAny.default_category_id,
        default_category_name: mrcAny.default_category?.name ?? null,
        created_by: row.created_by,
        created_by_name: row.created_by ? creatorMap.get(row.created_by) ?? null : null,
        created_at: row.created_at,
        similar: (sims ?? []).map((s) => ({
          id: s.id,
          name: s.name,
          icon: null,
          usage_count: 0,
          similarity: s.similarity,
        })),
      });
    }
  });

  return { items: results, total };
}

interface ModerationActionInput {
  queue_id: string;
  action: 'approve' | 'reject' | 'rename' | 'merge';
  merge_target_id?: string;
  new_name?: string;
  new_icon?: string;
  notes?: string;
}

interface ModerationActionResponse {
  action: string;
  affected_rows: number;
}

export async function moderationAction(input: ModerationActionInput): Promise<ModerationActionResponse> {
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData.session?.access_token;
  if (!token) throw new AppError('UNAUTHORIZED', 'Not authenticated', 401);

  const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/moderation-action`;
  const res = await fetch(url, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const json = (await res.json()) as EdgeResp<ModerationActionResponse>;
  if (!json.ok || !json.data) {
    throw new AppError(json.error?.code ?? 'UNKNOWN', json.error?.message ?? 'Unknown', res.status);
  }
  return json.data;
}

// ==================== Taxonomy management ====================

export interface TaxonomyItem {
  id: string;
  name: string;
  icon: string | null;
  moderation_status: string;
  is_import: boolean;
  /**
   * La creó una cuenta de pruebas automáticas (#33).
   *
   * El catálogo es el **único** sitio que sigue enseñándolas —fuera de aquí
   * están filtradas en cinco—, así que aquí hay que poder distinguirlas. Y desde
   * `20260815121023` hace más falta, no menos: esas filas nacen `pending` e
   * `is_import = false`, que es justo el filtro con el que se repasa el catálogo.
   */
  is_test: boolean;
  created_at: string;
  usage_count: number;
  created_by_name: string | null;
  // Sólo comercios: la categoría por defecto, resuelta con nombre e icono para
  // poder pintarla sin una consulta extra por fila.
  default_category_id?: string | null;
  default_category_name?: string | null;
  default_category_icon?: string | null;
}

export interface TaxonomySearchResult {
  id: string;
  name: string;
  icon: string | null;
  similarity: number;
  moderation_status: string;
  usage_count: number;
}

export async function fetchTaxonomyList(
  entityType: 'merchant' | 'category',
  search = '',
  limit = 100,
  offset = 0,
  statuses: string[] | null = null,
  usage: 'used' | 'unused' | null = null,
  imported: boolean | null = null,
): Promise<{ total: number; items: TaxonomyItem[] }> {
  const { data, error } = await supabase.rpc('admin_list_taxonomy', {
    p_entity_type: entityType,
    p_limit: limit,
    p_offset: offset,
    ...(search ? { p_search: search } : {}),
    ...(statuses?.length ? { p_statuses: statuses } : {}),
    ...(usage ? { p_usage: usage } : {}),
    ...(imported !== null ? { p_imported: imported } : {}),
  });
  if (error) {
    logger.error('fetchTaxonomyList failed', { code: error.code });
    throw new Error(error.message);
  }
  return data as unknown as { total: number; items: TaxonomyItem[] };
}

// Tope de elementos que devuelve admin_list_taxonomy en una llamada (clamp del
// RPC). Se usa para el "seleccionar todo lo filtrado".
export const TAXONOMY_SELECT_ALL_CAP = 200;

export async function searchTaxonomy(
  entityType: 'merchant' | 'category',
  query: string,
  limit = 10,
): Promise<TaxonomySearchResult[]> {
  if (query.length < 2) return [];
  const { data, error } = await supabase.rpc('search_taxonomy', {
    p_entity_type: entityType,
    p_query: query,
    p_limit: limit,
  });
  if (error) {
    logger.error('searchTaxonomy failed', { code: error.code });
    throw new Error(error.message);
  }
  return (data ?? []) as TaxonomySearchResult[];
}

export async function renameTaxonomy(
  entityType: 'merchant' | 'category',
  entityId: string,
  newName: string,
  newIcon?: string,
): Promise<void> {
  const { error } = await supabase.rpc('admin_rename_taxonomy', {
    p_entity_type: entityType,
    p_entity_id: entityId,
    p_new_name: newName,
    ...(newIcon ? { p_new_icon: newIcon } : {}),
  });
  if (error) {
    logger.error('renameTaxonomy failed', { code: error.code });
    throw new Error(error.message);
  }
}

export async function approveTaxonomy(
  entityType: 'merchant' | 'category',
  entityId: string,
): Promise<void> {
  const { error } = await supabase.rpc('admin_approve_taxonomy', {
    p_entity_type: entityType,
    p_entity_id: entityId,
  });
  if (error) {
    logger.error('approveTaxonomy failed', { code: error.code });
    throw new Error(error.message);
  }
}

export async function approveTaxonomyBatch(
  entityType: 'merchant' | 'category',
  entityIds: string[],
): Promise<number> {
  let n = 0;
  for (const id of entityIds) {
    await approveTaxonomy(entityType, id);
    n++;
  }
  return n;
}

export async function mergeTaxonomyBatch(
  entityType: 'merchant' | 'category',
  sourceIds: string[],
  targetId: string,
): Promise<number> {
  const rpcName = entityType === 'category' ? 'merge_category' : 'merge_merchant';
  let total = 0;
  for (const sourceId of sourceIds) {
    if (sourceId === targetId) continue;
    const { data, error } = await supabase.rpc(rpcName, {
      p_source_id: sourceId,
      p_target_id: targetId,
    });
    if (error) {
      logger.error('mergeTaxonomyBatch failed', { code: error.code, sourceId });
      throw new Error(error.message);
    }
    total += Number(data ?? 0);
  }
  return total;
}

export interface SetMerchantCategoryResult {
  updated_expenses: number;
  updated_rules: number;
}

/**
 * Cambia la categoría por defecto de un comercio y arrastra el histórico:
 * reasigna todos sus gastos vivos y sus reglas de recurrentes.
 *
 * Antes esto escribía `merchants.default_category_id` directamente, pero es un
 * campo derivado —un trigger lo recalcula desde las asociaciones comercio↔
 * categoría—, así que el cambio se perdía en cuanto alguien creaba un gasto con
 * ese comercio. El RPC colapsa las asociaciones, con lo que el valor derivado
 * pasa a coincidir con la realidad y el cambio se queda.
 *
 * `categoryId` null deja el comercio sin categoría por defecto y no toca los
 * gastos: `expenses.category_id` es NOT NULL.
 *
 * El recuento devuelto puede incluir gastos de grupos que el admin no ve: la
 * función corre como `SECURITY DEFINER` y la taxonomía es global.
 */
export async function setMerchantCategory(
  merchantId: string,
  categoryId: string | null,
): Promise<SetMerchantCategoryResult> {
  const { data, error } = await supabase.rpc('admin_set_merchant_category', {
    p_merchant_id: merchantId,
    // **Aquí no vale `?? undefined`**, aunque el vecino de `groups/api.ts` sí lo
    // use: este parámetro **no tiene valor por omisión** en SQL, así que
    // omitirlo sería llamar mal a la función. Y el nulo es una acción con
    // sentido —comprobado en el cuerpo de la RPC: con `p_category_id IS NULL`
    // borra las asociaciones y deja al comercio sin categoría por omisión—.
    //
    // El tipo generado dice `string` porque el generador de Supabase no sabe
    // expresar «obligatorio pero anulable»: lo que se pierde es la nulabilidad,
    // no la obligatoriedad.
    //
    // Se queda el `as` y no un `!`, que es lo que pide el linter: `categoryId!`
    // se lee como «prometo que no es nulo», y aquí es **falso y además al
    // revés** — el nulo es justo el caso interesante. Mentirle al tipo hay que
    // mentirle igual; lo que no hay que hacer es mentirle a quien lo lea.
    // eslint-disable-next-line @typescript-eslint/non-nullable-type-assertion-style
    p_category_id: categoryId as string,
  });
  if (error) {
    logger.error('setMerchantCategory failed', { code: error.code });
    throw new Error(error.message);
  }
  return (data ?? { updated_expenses: 0, updated_rules: 0 }) as unknown as SetMerchantCategoryResult;
}

// Sprint J.0: fetch categorías aprobadas para el picker de moderación.
// Cache 5min stale — cambian poco.
export async function fetchApprovedCategories(): Promise<
  { id: string; name: string; icon: string }[]
> {
  const { data, error } = await supabase
    .from('categories')
    .select('id, name, icon')
    .eq('moderation_status', 'approved')
    .order('name');
  if (error) {
    logger.error('fetchApprovedCategories failed', { code: error.code });
    return [];
  }
  return data ?? [];
}
